import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Button, ButtonVariant, Modal, Select, Show } from "shared/components";
import {
  lockboxesApi,
  Lockbox,
  GeneratedLockboxCode,
  SENTRILOCK_CODE_DURATIONS,
  SentrilockCodeDuration
} from "api/landlord/lockboxes";
import { getErrorMessage } from "utils/error";

const DURATION_LABELS: Record<SentrilockCodeDuration, string> = {
  30: "30 minutes",
  60: "1 hour",
  120: "2 hours",
  240: "4 hours"
};

const DURATION_OPTIONS = SENTRILOCK_CODE_DURATIONS.map((value) => ({
  label: DURATION_LABELS[value],
  value
}));

export const GenerateLockboxCodeModal = ({
  opened,
  onClose,
  onGenerated,
  lockbox
}: {
  opened: boolean;
  onClose: () => void;
  onGenerated: (result: GeneratedLockboxCode) => void;
  lockbox: Lockbox;
}) => {
  const isSentrilock = lockbox.vendor === "sentrilock";

  const [duration, setDuration] = useState<SentrilockCodeDuration>(
    SENTRILOCK_CODE_DURATIONS[0]
  );

  const { mutate: generate, isPending } = useMutation({
    mutationKey: ["LOCKBOX", "GENERATE_CODE", "CREATE", lockbox.id],
    mutationFn: () =>
      lockboxesApi.generateCode(
        lockbox.id,
        isSentrilock ? { duration_minutes: duration } : {}
      ),
    onSuccess: (result) => {
      onGenerated(result);
      setDuration(SENTRILOCK_CODE_DURATIONS[0]);
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  const handleClose = () => {
    // An in-flight request can't be aborted from here, so block close until it settles.
    if (isPending) return;
    setDuration(SENTRILOCK_CODE_DURATIONS[0]);
    onClose();
  };

  return (
    <Modal
      zIndex={52}
      opened={opened}
      onClose={handleClose}
      panelClassName="relative max-w-[366px] w-full space-y-4 border border-alpha-100 p-6 shadow-lg"
    >
      <p className="text-center text-xl font-bold leading-8 text-content-1000">
        Generate lockbox code
      </p>

      <div className="space-y-4">
        <Show if={isSentrilock}>
          <p className="text-center text-base text-content-500">
            How long should the code work?
          </p>
          <Select
            id="sentrilock-duration"
            aria-label="Code duration"
            options={DURATION_OPTIONS}
            value={duration}
            searchable={false}
            clearable={false}
            onChange={(e) =>
              setDuration(e.target.value as unknown as SentrilockCodeDuration)
            }
          />
          <div className="rounded-lg bg-warning-50 p-4">
            <p className="text-sm font-medium text-warning-700">
              Starts as soon as you generate it
            </p>
          </div>
        </Show>

        <Show if={!isSentrilock}>
          <div className="rounded-lg bg-warning-50 p-4">
            <p className="text-sm font-medium text-warning-700">
              Codebox codes work for the rest of today and can&apos;t be
              shortened or canceled once created.
            </p>
          </div>
        </Show>

        <div className="flex w-full gap-2">
          <Button className="w-full" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            className="w-full"
            variant={ButtonVariant.PRIMARY}
            onClick={() => generate()}
            loading={isPending}
            disabled={isPending}
          >
            Generate
          </Button>
        </div>
      </div>
    </Modal>
  );
};
